import * as PusherPushNotifications from "@pusher/push-notifications-web";
import Pusher from "pusher-js";
import { BASE_URL } from "@/constants";
import { store } from "@/store";
import type { Notification } from "@/types";

export const initializeBrowserPushNotifications = async (userId: number) => {
  if (!userId) {
    throw new Error("User ID is required for push notification initialization");
  }

  try {
    const beamsClient = new PusherPushNotifications.Client({
      instanceId: "691dd5ff-2d0d-435e-b1b8-3eaf7f441385",
    });

    try {
      const beamsUserId = await beamsClient.getUserId();
      console.log("Beams User ID:", beamsUserId);
      if (beamsUserId !== `user-${userId}`) {
        await beamsClient.stop();
      }
    } catch (error) {
      console.log("Error getting Beams User ID:", error);
    }

    const registrationState = await beamsClient.getRegistrationState();
    if (registrationState === "PERMISSION_GRANTED_REGISTERED_WITH_BEAMS") {
      console.log("Already registered with Beams");
    } else {
      // Ensure notification permission is granted before proceeding
      await requestNotificationPermission();
    }

    const state = store.getState();
    const clerkToken = await state.session.getTokenFn();
    const mobileToken = state.session.mobileToken;

    if (!clerkToken && !mobileToken) {
      throw new Error("Authentication token is required");
    }

    await beamsClient.start();
    const deviceId = await beamsClient.getDeviceId();
    console.log("Successfully registered with Beams. Device ID:", deviceId);

    const tokenProvider = new PusherPushNotifications.TokenProvider({
      url: `${BASE_URL}/push_notifications/token`,
      queryParams: {},
      headers: {
        [mobileToken ? "X-Access-Token" : "Authorization"]: `Bearer ${
          mobileToken || clerkToken
        }`,
        "Content-Type": "application/json",
      },
    });

    beamsClient
      .setUserId(`user-${userId}`, tokenProvider)
      .then(() => console.log("User ID has been set"))
      .catch((e) => console.error("Could not authenticate with Beams:", e));

    return beamsClient;
  } catch (error) {
    console.error("Error initializing push notifications:", error);
    throw error;
  }
};

export const initializeAppPushNotifications = async (userId: number) => {
  if (!userId) {
    throw new Error("User ID is required for push notification initialization");
  }

  const pusherClient = new Pusher("bddfee58a1281e760211", {
    cluster: "us2",
  });

  const channel = pusherClient.subscribe(`user-${userId}`);
  console.log(`Subscribed to channel ID: user-${userId}`);

  channel.bind("new_notification", (data: Notification) => {
    // Dispatch a custom event that the NotificationsMenu can listen to
    window.dispatchEvent(new CustomEvent("new-notification", { detail: data }));
  });

  return pusherClient;
};

export const requestNotificationPermission = async () => {
  if (!("Notification" in window)) {
    throw new Error("This browser does not support notifications");
  }

  const permission = await Notification.requestPermission();
  if (permission !== "granted") {
    throw new Error(
      "Please enable notifications to receive updates about new moves and important business notifications"
    );
  }
  return permission;
};
